import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14 } from "styled-components";
import React from "react";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { hasItems, takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type TextMediaCItemRowViewProps = {
  description?: React.ReactNode;
  media?: React.ReactNode;
  title?: string | null;
};
export function TextMediaCItemRowView({
  description,
  media,
  title
}: TextMediaCItemRowViewProps) {
  return <_StyledDiv>
      {media && <_StyledDiv2>{media}</_StyledDiv2>}
      <_StyledDiv3>
        {title && <_StyledP>{title}</_StyledP>}
        {description}
      </_StyledDiv3>
    </_StyledDiv>;
}
export type TextMediaCBlockViewProps<TItem = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (item: TItem, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  items?: TItem[] | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mediaPosition?: string | null;
  mode?: string | null;
  renderItem: (item: TItem, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function TextMediaCBlockView<TItem = unknown>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  headline,
  items = [],
  mediaDesktop,
  mediaMobile,
  mediaPosition = "right",
  mode = "light",
  renderItem,
  sectionProps,
  text,
  theme = "white"
}: TextMediaCBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // v3: white theme renders true white (the theme helper maps "white" to grey-20).
  const surface = theme as Theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  const rows = items ?? [];
  const textCol = <_StyledDiv4 key="text-col" $_css={[{
    "gridColumn": "1 / -1",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 4 / span 4"
    }
  }]}>
      <_StyledDiv5 $_css2={[{
      "position": "relative",
      "display": "flex",
      "height": "100%",
      "flexDirection": "column",
      "padding": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "justifyContent": "space-between",
        "padding": "var(--spacing-3xl)"
      }
    }, surface, themeStyle.border]}>
        <_StyledDiv6>
          {headline && <_StyledHeadline type="h2" text={headline} />}
          {text}
        </_StyledDiv6>

        {hasItems(rows) && <_StyledDiv7 $_css3={[{
        "marginTop": "4rem",
        "display": "flex",
        "flexDirection": "column",
        "gap": "var(--spacing-sm)",
        "borderRadius": "8px",
        "@media (min-width: 1024px)": {
          "marginTop": "8rem"
        }
      }, themeStyle.border]}>
            {rows.map((item, idx) => <React.Fragment key={getItemKey?.(item, idx) || idx}>{renderItem(item, idx)}</React.Fragment>)}
          </_StyledDiv7>}
      </_StyledDiv5>

      <_StyledDiv8 $_css4={[{
      "display": "block",
      "borderBottomWidth": "1px",
      "padding": "1.6rem",
      "@media (min-width: 1024px)": {
        "display": "none"
      }
    }, themeStyle.border, surface]}>{mediaMobile}</_StyledDiv8>
    </_StyledDiv4>;
  const mediaCol = <_StyledDiv9 key="media-col" $_css5={[{
    "position": "relative",
    "gridColumn": "span 8 / span 8",
    "display": "none",
    "padding": "4rem",
    "@media (min-width: 1024px)": {
      "display": "block"
    }
  }, themeStyle.border, surface]}>
      <_StyledDiv10>{mediaDesktop}</_StyledDiv10>
    </_StyledDiv9>;
  const columns = mediaPosition === "right" ? [textCol, mediaCol] : [mediaCol, textCol];
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css6={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, surface]}>
      <Wrap mode={mode as Mode}>
        <_StyledGrid $_css7={[{
        "@media (min-width: 1024px)": {
          "minHeight": "650px"
        }
      }, themeStyle.border]}>{columns}</_StyledGrid>
      </Wrap>
    </_StyledSection>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-1z03m9h-0"
})({
  "display": "flex",
  "alignItems": "flex-start",
  "gap": "var(--spacing-md)",
  "borderRadius": "4px",
  "backgroundColor": "var(--bg-primary)",
  "padding": "var(--spacing-sm)"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1z03m9h-1"
})({
  "height": "4.8rem",
  "width": "4.8rem",
  "flexShrink": "0",
  "overflow": "hidden",
  "borderRadius": "2px"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1z03m9h-2"
})({
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-xs)"
});
var _StyledP = _styled4("p").withConfig({
  componentId: "sc-sf-1z03m9h-3"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1z03m9h-4"
})`${p => p.$_css}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-1z03m9h-5"
})`${p => p.$_css2}`;
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-1z03m9h-6"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-lg)"
});
var _StyledHeadline = _styled8(Headline).withConfig({
  componentId: "sc-sf-1z03m9h-7"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-1z03m9h-8"
})`${p => p.$_css3}`;
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-1z03m9h-9"
})`${p => p.$_css4}`;
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-1z03m9h-10"
})`${p => p.$_css5}`;
var _StyledDiv10 = _styled12("div").withConfig({
  componentId: "sc-sf-1z03m9h-11"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledSection = _styled13("section").withConfig({
  componentId: "sc-sf-1z03m9h-12"
})`${p => p.$_css6}`;
var _StyledGrid = /*#__PURE__*/_styled14(Grid).withConfig({
  displayName: "text-media-c___StyledGrid",
  componentId: "sc-ihfghv-0"
})(["", ""], p => p.$_css7);